import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import TooltipSimple from '../../common/tooltips/TooltipSimple.jsx';
import TextCopyable from '../../common/TextCopyable.jsx';
import CardClient from '../../common/cards/CardClient.jsx';
import ButtonCircle from '../../common/buttons/ButtonCircle.jsx';
import ButtonCircleDropdown from '../../common/buttons/ButtonCircleDropdown.jsx';
import BadgeStatus from '../../common/badges/BadgeStatus.jsx';
import BadgeManager from '../../common/badges/BadgeManager.jsx';
import CardManager from '../cards/CardManager';
import FrameworkAgreementsKpis from './FrameworkAgreementsKpis';

const today = new Date().toISOString().split('T')[0];

const FrameworkAgreementsDetails = (props) => {

    const context = props.context;
    const frameworkAgreementId = props.frameworkAgreementId;
    const containerType = props.containerType || 'card';
    const showActionButton = props.showActionButton || false;

    const [loading, setLoading] = useState(true);
    const [frameworkAgreement, setFrameworkAgreement] = useState(props.frameworkAgreement ?? null);
    const [editUrlTarget, setEditUrlTarget] = useState();

    const { t: gctrad } = useTranslation();

    // TODO À mettre ailleurs ?
    const FRAMEWORK_AGREEMENTS_STATUS_TO_WRITE = 'fa_to_write';
    const FRAMEWORK_AGREEMENTS_STATUS_TO_BE_SIGNED = 'fa_to_be_signed';
    const FRAMEWORK_AGREEMENTS_STATUS_AWAITING_SIGNATURE = 'fa_awaiting_signature';
    const FRAMEWORK_AGREEMENTS_STATUS_SIGNED = 'fa_signed';
    const FRAMEWORK_AGREEMENTS_STATUS_TERMINATED = 'fa_terminated';
    const FRAMEWORK_AGREEMENTS_STATUS_CANCELLED = 'fa_cancelled';
    const FRAMEWORK_AGREEMENTS_VERSIONS_STATUS_DRAFT = 'fav_draft';
    const FRAMEWORK_AGREEMENTS_VERSIONS_STATUS_IN_PROGRESS = 'fav_in_progress';
    const FRAMEWORK_AGREEMENTS_VERSIONS_STATUS_MODIFIED = 'fav_modified';

    let buttonCircleDropdownItems = [
		{
			'href': `providers//framework_agreements/terminate/${frameworkAgreementId}`,
			'text': gctrad('Résilier le contrat'),
			'class': 'text-danger',
		}
	];

    useEffect(() => {

        if ( 'undefined' != typeof frameworkAgreementId ) {
            let environment = ENV.url.includes('/preview/') ? 'preview' : 'myspace';
            let url = `${ENV.base_lang_url}${environment}/${context}//framework_agreements/ajax_get_one/${frameworkAgreementId}`;
            fetch(url)
                .then(res => res.json())
                .then(result => {
                    setFrameworkAgreement(result.frameworkAgreement);
                    setLoading(false);
                });
        }
        else if ( 'undefined' != typeof frameworkAgreement ) {
            setFrameworkAgreement(frameworkAgreement);
            setLoading(false);
        } // endif

        let action = 'view';
        if ( 'providers' == context && [FRAMEWORK_AGREEMENTS_VERSIONS_STATUS_DRAFT].includes(frameworkAgreement?.version.status) ) action = 'edit';
        setEditUrlTarget(`${context}//framework_agreements/${action}/${frameworkAgreement?.agreement.id}`);

    }, []);

    return (
        <div className="framework_agreements-details">
            {loading ? (
                <div className="row">
                    <div className="col-12">
                        <Skeleton className={`card border-none ${'list' == containerType ? 'bg-transparent' : 'bg-gc-light-blue-dark'}`} variant="rectangular" width="100%" height={'list' == containerType ? 164 : 195} />
                    </div>
                </div>
            ) : (

                <>

                    {/* Entête */}
                    <div className="d-block d-md-flex justify-content-between align-items-center">

                            <div className="order-last order-md-first">

                                <div className="mb-1">

                                    <div className="d-flex align-items-center" style={{'gap': '.5rem'}}>

                                        {/* Nom du contrat */}
                                        <h2 className="h4 font-weight-bold m-0 mb-1">
                                            <TooltipSimple title={frameworkAgreement.agreement.title}>
                                                <span>
                                                    {frameworkAgreement.agreement.title.truncate(50)}
                                                </span>
                                            </TooltipSimple>
                                        </h2>

                                        {/* Dates de validité du contrat */}
                                        <span className="h6 font-weight-normal m-0">
                                            {frameworkAgreement.agreement.date_end == frameworkAgreement.agreement.date_start ?
                                                gctrad('[:gps] Le %1').replace('%1', (new Date(frameworkAgreement.agreement.date_start)).toLocaleDateString())
                                                :
                                                gctrad('[:gps] Du %1 au %2').replace('%1', (new Date(frameworkAgreement.agreement.date_start)).toLocaleDateString()).replace('%2', (new Date(frameworkAgreement.agreement.date_end)).toLocaleDateString())
                                            }
                                        </span>

                                        {/* Responsable du contrat */}
                                        {'providers' == context &&
                                            <BadgeManager manager={frameworkAgreement.manager} bgColor={'light'} />
                                        }

                                    </div>

                                </div>

                                {/* Informations sur le contrat */}
                                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>

                                    <TooltipSimple title={gctrad("Copier le nom de l'entreprise")}>
                                        <span className="small font-weight-normal d-inline-block">
                                            <TextCopyable
                                                text={frameworkAgreement.company.name.truncate(50)}
                                                value={frameworkAgreement.company.name}
                                                icon={<i className="fa-regular fa-briefcase mr-1"></i>}
                                            />
                                        </span>
                                    </TooltipSimple>

                                    <TooltipSimple title={gctrad("Date de création")}>
                                        <span className="small font-weight-normal d-inline-block">
                                            <TextCopyable
                                                text={frameworkAgreement.agreement.date_created.formatDateTime({ year: 'numeric', month: 'numeric', day: 'numeric'})}
                                                value={frameworkAgreement.agreement.date_created}
                                                icon={<i className="fa-regular fa-calendars mr-1"></i>}
                                            />
                                        </span>
                                    </TooltipSimple>

                                    {frameworkAgreement.agreement.ref_client &&
                                        <TooltipSimple title={gctrad("Copier la référence du contrat")}>
                                            <span className="small font-weight-normal d-inline-block">
                                                <TextCopyable
                                                    text={frameworkAgreement.agreement.ref_client}
                                                    value={frameworkAgreement.agreement.ref_client}
                                                    icon={<i className="fas fa-hashtag mr-1"></i>}
                                                />
                                            </span>
                                        </TooltipSimple>
                                    }

                                    <span className="small font-weight-normal d-inline-block">
                                        <i className="fa-solid fa-arrows-rotate mr-1"></i>
                                        {gctrad('Version %1').replace('%1', frameworkAgreement.version.version)} ({frameworkAgreement.version.date_updated.formatDateTime({ year: 'numeric', month: 'numeric', day: 'numeric'})})
                                    </span>

                                    <span className="small font-weight-normal d-inline-block">
                                        <i className="fa-regular fa-hotel mr-1"></i>
                                        {(1 < frameworkAgreement.hotels.length ? gctrad('%1 établissements') : gctrad('%1 établissement')).replace('%1', frameworkAgreement.hotels.length)}
                                    </span>

                                    {/* Edition des propriétés du contrat */}
                                    {('providers' == context && ![FRAMEWORK_AGREEMENTS_STATUS_TERMINATED, FRAMEWORK_AGREEMENTS_STATUS_CANCELLED].includes(frameworkAgreement.agreement.status)) &&
                                                    <ButtonCircle 
                                            href={`providers//framework_agreements/edit_settings/${frameworkAgreement.agreement.id}`}
                                            part='+'
                                            icon={<i className="fa-regular fa-pen-to-square"></i>}
                                            tooltip={true}
                                            tooltipText={gctrad('Gérer le contrat')}
                                        />
                                    }

                                    {/* Bouton de résiliation du contrat */}
                                    {('providers' == context && [FRAMEWORK_AGREEMENTS_STATUS_SIGNED].includes(frameworkAgreement.agreement.status)) &&
                                        <span className="d-inline-block mr-2">
                                            <ButtonCircleDropdown 
                                                href={'#'} 
                                                icon={<i className="fa-regular fa-ellipsis-vertical"></i>}
                                                items={buttonCircleDropdownItems}
                                            />  
                                        </span>
                                    }

                                </div>
                                
                            </div>

                            {/* Statut du contrat */}
                            <div className="order-first order-md-last text-left text-md-right mt-4 mt-md-0">
                                <BadgeStatus badge={frameworkAgreement.status.badge} />
                            </div>

                    </div>

                    {/* Contenu client */}
                    {'card' == containerType && 'clients' == context && 
                        <div className={`card border-none mt-3 ${'list' == containerType ? 'p-0 bg-transparent' : 'p-3 bg-light'}`}>

                            <div className="card-body text-gc-dark-blue p-0 d-flex" style={{'gap': '0.5rem'}}>

                                <div className="card bg-transparent border-none d-flex flex-row align-items-center justify-content-center w-25 p-3">
                                    {frameworkAgreement.societe?.logo &&
                                        <img src={frameworkAgreement.societe.logo.url} width={frameworkAgreement.societe.logo.width} height={frameworkAgreement.societe.logo.height} style={{width: 'auto', maxHeight: '68px'}} />
                                    }
                                </div>
        
                                <div className="row d-flex w-100 m-0" style={{'gap': '.5rem'}}>
                                    <div className="col">
                                        <CardManager manager={frameworkAgreement.manager} />
                                    </div>
                                    <div className="col card bg-white border-none d-flex flex-row align-items-center p-3">
                                        <div>
                                            <span className="d-block h6 font-weight-bold m-0">{frameworkAgreement.societe.raison_sociale}</span>
                                            <span className="d-block small font-weight-normal m-0" style={{lineHeight: '1rem'}}>{frameworkAgreement.societe.address}</span>
                                            <span className="d-block small font-weight-normal m-0" style={{lineHeight: '1rem'}}>{frameworkAgreement.societe.postalcode} {frameworkAgreement.societe.city}</span>
                                            <span className="d-block small font-weight-normal m-0" style={{lineHeight: '1rem'}}>{frameworkAgreement.societe.pays_nom}</span>
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>
                    }

                    {/* Contenu hôtelier */}
                    {('list' == containerType || 'providers' == context) &&
                        <div className={`card border-none mt-3 ${'list' == containerType ? 'p-0 bg-transparent' : 'p-3 bg-light'}`}>

                            <div className="card-body text-gc-dark-blue p-0">

                                <div className="d-flex justify-content-between" style={{'gap': '1rem'}}>

                                    {/* Client ou manager */}
                                    <div className="w-25" style={{minWidth: '240px'}}>
                                        {'clients' == context ?
                                            <CardManager 
                                                manager={frameworkAgreement.manager} 
                                                textColor={frameworkAgreement?.manager?.textColor}
                                                icon={frameworkAgreement?.manager?.icon}
                                                othersManagersCount={frameworkAgreement?.manager?.othersManagersCount}
                                            />
                                        :
                                            <a href="#" data-part="+" data-target={`providers//clients/profile/${frameworkAgreement.client.id}`} className="js-nav get_part_link text-decoration-none">
                                                <CardClient client={frameworkAgreement.client} details={true} />
                                            </a>
                                        }
                                    </div>

                                    {/* KPIs */}
                                    {'providers' == context &&
                                        <FrameworkAgreementsKpis frameworkAgreement={frameworkAgreement} context={context} />
                                    }
                                    
                                    {/* Bouton "Voir le contrat" */}
                                    {showActionButton &&
                                        <a href="#" data-part="+" data-target={editUrlTarget} className="js-nav get_part_link">
                                            <div className="card border-none bg-white p-3">
                                                <div className="d-flex align-items-center text-center m-auto h5 px-3 text-nowrap font-weight-bold">
                                                    {gctrad('Voir le contrat')}
                                                </div>
                                            </div>
                                        </a>
                                    }

                                </div>

                            </div>

                        </div>
                    }

                </>

            )}
        </div>
    );

}

export default FrameworkAgreementsDetails;
